import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';

const services = [
  {
    title: 'Consulting',
    heading: 'Infor WMS Consulting',
    description:
      'We provide strategic consulting to help you optimize your warehouse operations with Infor WMS. Our team conducts comprehensive warehouse process assessments to identify improvement opportunities, develops solution designs and architectures aligned with your operational goals, creates warehouse optimization strategies, and delivers digital transformation consulting. We work closely with your leadership to define clear roadmaps that ensure your WMS system supports your operational objectives while enabling scalability and future growth.',
    points: [
      'Warehouse process assessment',
      'Solution design & architecture',
      'Warehouse optimization strategy',
    ],
  },
  {
    title: 'Implementation',
    heading: 'Infor WMS Implementation',
    description:
      'We manage end-to-end Infor WMS implementations from project kickoff to go-live and beyond. Our implementation methodology covers warehouse configuration, business process setup, go-live planning and deployment, and comprehensive user acceptance testing (UAT). We ensure that every aspect of your warehouse operations—from receiving to shipping—is properly configured to support your unique business processes. Our team works alongside your project team to establish realistic timelines, manage risks proactively, and deliver a system that drives operational excellence from day one.',
    points: [
      'End-to-end WMS implementation',
      'Warehouse configuration & process setup',
      'Go-live planning & UAT',
    ],
  },
  {
    title: 'Process Optimization',
    heading: 'Warehouse Process Optimization',
    description:
      'We analyze and optimize your warehouse processes to improve efficiency, accuracy, and productivity within Infor WMS. Our optimization services cover receiving optimization, put-away strategies, inventory optimization, picking and packing optimization, shipping process improvement, cross-docking implementation, and wave planning optimization. We use data analysis and process mapping to identify bottlenecks, eliminate waste, and implement best-practice workflows. Our recommendations are grounded in real-world experience and designed to deliver measurable improvements in operational performance and cost reduction.',
    points: [
      'Receiving, put-away & inventory optimization',
      'Picking, packing & shipping improvement',
      'Cross-docking & wave planning optimization',
    ],
  },
  {
    title: 'Inventory Management',
    heading: 'Inventory Management Solutions',
    description:
      'We help you achieve real-time inventory visibility and control with Infor WMS. Our inventory management solutions include lot and serial number tracking, multi-warehouse inventory management, cycle counting, stock replenishment, and inventory accuracy improvement. We implement best-practice inventory processes that reduce carrying costs, minimize stockouts, and improve order fulfillment accuracy. Our approach ensures you have the right inventory in the right place at the right time, enabling better decision-making and improved customer satisfaction.',
    points: [
      'Real-time inventory visibility & tracking',
      'Multi-warehouse inventory management',
      'Cycle counting & stock replenishment',
    ],
  },
  {
    title: 'Integration',
    heading: 'Infor WMS Integration Services',
    description:
      'We connect Infor WMS with surrounding applications and systems to create a unified warehouse ecosystem. Our integration services include ERP integration (Infor LN, M3, CSI), Infor OS integration, API development, e-commerce integration, Transportation Management System (TMS) integration, MES integration, EDI integration, and third-party logistics (3PL) integration. We design robust integration architectures that ensure reliable data flow, handle exceptions effectively, and provide real-time synchronization where needed, creating a seamless digital experience across your supply chain.',
    points: [
      'ERP, Infor OS & API integration',
      'E-commerce, TMS & MES integration',
      'EDI & 3PL integration',
    ],
  },
  {
    title: 'Automation Integration',
    heading: 'Warehouse Automation Integration',
    description:
      'We integrate Infor WMS with warehouse automation technologies to maximize operational efficiency and accuracy. Our automation integration services include barcode scanning, RFID integration, voice picking, conveyor system integration, AS/RS integration, pick-to-light systems, and robotics integration. We also provide warehouse automation consulting to help you identify the right automation technologies for your operations. Our approach ensures seamless integration between your WMS and automation systems, enabling higher throughput, reduced errors, and improved labor productivity.',
    points: [
      'Barcode scanning, RFID & voice picking',
      'Conveyor, AS/RS & pick-to-light integration',
      'Robotics integration & automation consulting',
    ],
  },
  {
    title: 'Cloud Migration & Upgrades',
    heading: 'Cloud Migration & Upgrades',
    description:
      'We help organizations modernize their Infor WMS environment with structured cloud migration, version upgrades, and system modernization. Our services include cloud migration planning and execution, version upgrades, system modernization, performance optimization, and upgrade testing. We conduct thorough pre-migration assessments to identify potential challenges, develop comprehensive migration strategies, and provide post-migration support to ensure a smooth transition. Our expertise ensures you unlock the full benefits of cloud capabilities and new features while maintaining system integrity and performance.',
    points: [
      'Cloud migration & version upgrades',
      'System modernization & performance optimization',
      'Upgrade testing',
    ],
  },
  {
    title: 'Reporting & Analytics',
    heading: 'Reporting & Analytics',
    description:
      'We help you leverage data to drive informed decision-making with comprehensive reporting and analytics solutions for Infor WMS. Our services include warehouse dashboards, KPI reporting, inventory analytics, labor analytics, operational reporting, executive dashboards, and embedded analytics. We design dashboards and reports that provide real-time visibility into warehouse operations, enabling you to identify trends, spot issues early, and make data-driven decisions. Our analytics solutions help you measure performance, optimize operations, and achieve continuous improvement.',
    points: [
      'Warehouse dashboards & KPI reporting',
      'Inventory & labor analytics',
      'Operational & executive reporting',
    ],
  },
//   {
//     title: 'Labor Management',
//     heading: 'Labor Management',
//     description:
//       'We help you optimize workforce productivity with comprehensive labor management solutions for Infor WMS. Our services include workforce planning, labor productivity tracking, task management, performance monitoring, and workforce optimization. We implement best-practice labor management processes that improve employee engagement, reduce labor costs, and increase operational efficiency. Our approach provides real-time visibility into labor performance, enabling better resource allocation, improved productivity, and enhanced employee accountability.',
//     points: [
//       'Workforce planning & productivity tracking',
//       'Task management & performance monitoring',
//       'Workforce optimization',
//     ],
//   },
  {
    title: 'Support & Managed Services',
    heading: 'Support & Managed Services',
    description:
      'We provide comprehensive 24×7 application support for Infor WMS including incident management, performance monitoring, preventive maintenance, SLA-based support, and continuous improvements. Our support model includes tiered escalation, proactive monitoring, and regular system health checks to keep your warehouse operations running smoothly. We work collaboratively with your internal teams to build knowledge and reduce dependency over time, while ensuring your system remains optimized and aligned with evolving business requirements. Regular support reviews help prioritize improvements for maximum business impact.',
    points: [
      '24×7 application support',
      'Incident management & performance monitoring',
      'Preventive maintenance & continuous improvements',
    ],
  },
  {
    title: 'Training & Enablement',
    heading: 'Training & User Enablement',
    description:
      'We prepare your teams for success with Infor WMS through comprehensive training and enablement programs. Our training services include end-user training, administrator training, warehouse operator training, technical training, documentation, and knowledge transfer. We design role-based training that focuses on practical scenarios, daily operations, and exception handling so users understand both the system and the processes behind it. Our change management approach ensures teams are truly ready to embrace new workflows, driving adoption and accelerating time-to-value for your Infor WMS investment.',
    points: [
      'End-user & warehouse operator training',
      'Administrator & technical training',
      'Documentation & knowledge transfer',
    ],
  },
];

const WMSOverview = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeService = services[activeIndex];

  return (
    <section
      id="wms-overview"
      className="px-4 py-16 sm:px-6 lg:px-20 lg:py-20"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="mx-auto max-w-8xl">
        <div className="text-center">
          <h2
            className="text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl"
            style={{ color: '#1A2634' }}
          >
            Our Infor WMS Consulting Services
          </h2>
          <p
            className="mt-4 text-base text-center sm:text-lg"
            style={{ color: '#000000', fontWeight: 400 }}
          >
            Expert solutions to maximize your Infor WMS investment.
          </p>
        </div>

        <div className="mt-12">
          <h3
            className="text-3xl font-bold sm:text-4xl"
            style={{ color: '#1A2634' }}
          >
            Infor WMS Expertise
          </h3>

          <div className="mt-8 grid gap-8 lg:grid-cols-[0.8fr_1.45fr] lg:items-start">
            <div
              className="space-y-1 border-l-4 py-2"
              style={{ borderColor: '#F0F0F0' }}
            >
              {services.map((service, index) => {
                const isActive = index === activeIndex;

                return (
                  <button
                    key={service.title}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className="block w-full px-5 py-2.5 text-left text-sm font-bold transition duration-300 sm:text-base"
                    style={{
                      color: isActive ? '#0F172A' : '#1F2937',
                      borderLeft: isActive ? '4px solid #D81820' : '4px solid transparent',
                      transform: isActive ? 'translateX(-4px)' : 'translateX(0)',
                    }}
                  >
                    {service.title}
                  </button>
                );
              })}
            </div>

            <div
              className="rounded-lg border p-6 sm:p-7 lg:p-8"
              style={{
                backgroundColor: '#FFFFFF',
                borderColor: 'rgba(15, 23, 42, 0.06)',
                boxShadow: '0 24px 70px rgba(15, 23, 42, 0.08)',
              }}
            >
              <p
                className="wms-overview-description w-full max-w-none md:text-md sm:text-lg"
                style={{
                  color: '#000000',
                  fontWeight: 400,
                  textAlign: 'justify',
                  textJustify: 'inter-word',
                  wordBreak: 'normal',
                  overflowWrap: 'normal',
                  hyphens: 'none',
                  whiteSpace: 'normal',
                }}
              >
                {activeService.description}
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {activeService.points.map((point) => (
                  <div
                    key={point}
                    className="flex gap-2.5 rounded-lg p-3"
                    style={{ backgroundColor: '#F8FAFC' }}
                  >
                    <CheckCircle2
                      className="mt-0.5 h-4 w-4 shrink-0"
                      style={{ color: '#D81820' }}
                    />
                    <p
                      className="text-xs font-medium leading-5 sm:text-sm"
                      style={{ color: '#1A2634', fontWeight: 500 }}
                    >
                      {point}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WMSOverview;